/* Earnings calendar helper.
   - fetchEarnings(ticker)            → {ticker, next_earnings_date, days_until, last_filing_date}
   - fetchEarningsBulk(tickers[])     → array of the same
   - useVaultEarnings(tickers)        → {map: {TICKER: earnings}, loading}
*/
import { useEffect, useState, useRef } from "react";

const API = process.env.REACT_APP_BACKEND_URL || "";

export async function fetchEarnings(ticker) {
  if (!ticker) return null;
  try {
    const res = await fetch(`${API}/api/stock/earnings/${encodeURIComponent(ticker.toUpperCase())}`, { credentials: "include" });
    if (!res.ok) return null;
    return await res.json();
  } catch { return null; }
}

export async function fetchEarningsBulk(tickers) {
  const syms = (tickers || []).map(t => String(t).toUpperCase().trim()).filter(Boolean);
  if (syms.length === 0) return [];
  try {
    const qs = new URLSearchParams({ tickers: syms.join(",") });
    const res = await fetch(`${API}/api/stock/earnings/bulk?${qs.toString()}`, { credentials: "include" });
    if (!res.ok) return [];
    const data = await res.json();
    return Array.isArray(data?.results) ? data.results : [];
  } catch { return []; }
}

/** Hook: watches a ticker list and returns a map { TICKER: earnings-entry }.
 *  Refetches when the ticker list changes (shallow compare on joined string). */
export function useVaultEarnings(tickers) {
  const [map, setMap] = useState({});
  const [loading, setLoading] = useState(false);
  const keyRef = useRef("");
  const key = (tickers || []).map(t => String(t).toUpperCase()).sort().join(",");

  useEffect(() => {
    if (!key || key === keyRef.current) return;
    keyRef.current = key;
    let cancelled = false;
    (async () => {
      setLoading(true);
      const results = await fetchEarningsBulk(key.split(","));
      if (cancelled) return;
      const m = {};
      for (const r of results) m[r.ticker] = r;
      setMap(m);
      setLoading(false);
    })();
    return () => { cancelled = true; };
  }, [key]);

  return { map, loading };
}
